import { useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { money } from "@/lib/format";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PaidByBadge } from "@/components/lumper/LumperPanel";
import { AccessorialDialog, LumperEditDialog } from "./AccessorialDialogs";

const accName = (a) => (a.type === "Other" && a.label ? a.label : a.type);

const IconBtn = ({ label, onClick, danger, testId, children }) => (
  <Button variant="ghost" size="icon" onClick={onClick} aria-label={label} data-testid={testId}
    className={`h-8 w-8 text-slate-400 ${danger ? "hover:text-rose-600 hover:bg-rose-50" : "hover:text-[#002FA7]"}`}>{children}</Button>
);

const Line = ({ title, sub, amount, muted, actions, testId, badge }) => (
  <div className="flex items-start gap-3 py-2.5 border-t border-slate-100 first:border-t-0" data-testid={testId}>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium flex flex-wrap items-center gap-1.5">{title}{badge}</div>
      {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
    </div>
    <div className={`text-sm font-semibold tabular-nums whitespace-nowrap ${muted ? "text-slate-400 line-through decoration-slate-300" : ""}`}>{amount}</div>
    {actions && <div className="flex -mr-1">{actions}</div>}
  </div>
);

export const Accessorials = ({ load, locked, onChanged, onOpenDoc, onDeleteDoc }) => {
  const { getToken } = useAuth();
  const [editing, setEditing] = useState(null);
  const [lumper, setLumper] = useState(null);
  const [removing, setRemoving] = useState(null);
  const api = () => makeApi(getToken);
  const lumpers = (load.documents || []).filter((d) => d.type === "Lumper");
  const accs = load.accessorials || [];

  const saveAcc = async (body) => {
    if (editing.id) await api().patch(`/loads/${load.id}/accessorials/${editing.id}`, body);
    else await api().post(`/loads/${load.id}/accessorials`, body);
    toast.success(editing.id ? "Charge updated" : "Charge added");
    onChanged();
  };
  const saveLumper = async (body) => { await api().patch(`/documents/${lumper.id}/lumper`, body); toast.success("Lumper receipt updated"); onChanged(); };
  const removeAcc = async () => {
    try { await api().delete(`/loads/${load.id}/accessorials/${removing.id}`); toast.success("Charge removed"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
    setRemoving(null);
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="accessorials-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Accessorials</h2>
        {locked ? (
          <span className="pill bg-slate-100 text-slate-600 border border-slate-200" data-testid="accessorials-locked"><Lock className="w-3 h-3" /> Invoice sent, locked</span>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid="add-accessorial-btn"><Plus className="w-4 h-4 mr-1" /> Add charge</Button>
        )}
      </div>
      <div className="mt-3" data-testid="accessorial-list">
        {accs.length === 0 && lumpers.length === 0 && <p className="text-sm text-slate-500 py-2">No accessorials yet. Lumper receipts the driver uploads show up here too.</p>}
        {accs.map((a) => (
          <Line key={a.id} testId={`acc-row-${a.id}`} title={accName(a)} sub={a.note} amount={money(a.amount)}
            actions={!locked && <>
              <IconBtn label={`Edit ${accName(a)}`} onClick={() => setEditing(a)} testId={`acc-edit-${a.id}`}><Pencil className="w-4 h-4" /></IconBtn>
              <IconBtn label={`Delete ${accName(a)}`} danger onClick={() => setRemoving(a)} testId={`acc-delete-${a.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
            </>} />
        ))}
        {lumpers.map((d) => {
          const billed = d.paid_by === "carrier" && d.amount > 0;
          return (
            <Line key={d.id} testId={`lumper-row-${d.id}`} title="Lumper receipt" badge={<PaidByBadge doc={d} />}
              sub={<button type="button" onClick={() => onOpenDoc(d)} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline" data-testid={`lumper-open-${d.id}`}><FileText className="w-3 h-3" />{d.original_filename}{d.uploaded_by_name ? ` · by ${d.uploaded_by_name}` : ""}</button>}
              amount={d.amount ? money(d.amount) : "-"} muted={!billed}
              actions={!locked && <>
                <IconBtn label="Edit lumper receipt" onClick={() => setLumper(d)} testId={`lumper-edit-${d.id}`}><Pencil className="w-4 h-4" /></IconBtn>
                <IconBtn label="Delete lumper receipt" danger onClick={() => onDeleteDoc(d)} testId={`lumper-delete-${d.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
              </>} />
          );
        })}
      </div>
      {lumpers.some((d) => d.paid_by === "carrier") && <p className="mt-2 text-xs text-amber-800" data-testid="lumper-billback-note">&quot;We paid&quot; lumpers are billed back on the invoice. &quot;Broker paid&quot; ones are for the record only.</p>}
      <div className="mt-4 pt-3 border-t border-slate-200 space-y-1 text-sm">
        <div className="flex justify-between text-slate-500"><span>Linehaul rate</span><span className="tabular-nums" data-testid="billing-rate">{money(load.rate)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Accessorials subtotal</span><span className="tabular-nums" data-testid="billing-acc-subtotal">{money(load.accessorials_total)}</span></div>
        <div className="flex justify-between items-baseline pt-1"><span className="font-semibold">Total billable</span><span className="text-xl font-bold text-[#002FA7] tabular-nums" style={{ fontFamily: "Outfit" }} data-testid="billing-total">{money(load.billable_total)}</span></div>
      </div>
      <AccessorialDialog item={editing} onClose={() => setEditing(null)} onSave={saveAcc} />
      <LumperEditDialog doc={lumper} onClose={() => setLumper(null)} onSave={saveLumper} />
      <ConfirmDialog open={!!removing} title="Remove this charge?" description={removing ? `${accName(removing)} · ${money(removing.amount)} will be removed from this load.` : ""} confirmLabel="Remove" onConfirm={removeAcc} onCancel={() => setRemoving(null)} testId="acc-delete-confirm" />
    </Card>
  );
};
